import type { FolderTreeItem } from '@platform/ui';

interface FolderLocation { folderId: string; rootId: string; path: string }

/** Discovered ancestry is the only store authority; native IDs remain opaque. */
function locations(folders: readonly FolderTreeItem[]): ReadonlyMap<string, FolderLocation> {
  const byId = new Map<string, FolderTreeItem>();
  const aliases = new Map<string, string>();
  const ambiguous = new Set<string>();
  for (const folder of folders) {
    if (byId.has(folder.id)) ambiguous.add(folder.id);
    byId.set(folder.id, folder);
    for (const alias of [folder.id, folder.nativeId].filter((id): id is string => Boolean(id))) {
      const previous = aliases.get(alias);
      if (previous && previous !== folder.id) ambiguous.add(alias);
      aliases.set(alias, folder.id);
    }
  }
  const result = new Map<string, FolderLocation>();
  for (const folder of folders) {
    const path: FolderTreeItem[] = [];
    const seen = new Set<string>();
    let current: FolderTreeItem | undefined = folder;
    while (current && !seen.has(current.id) && !ambiguous.has(current.id)) {
      seen.add(current.id);
      path.unshift(current);
      if (current.mailboxRoot && current.selectable === false) {
        const root = current;
        const rootName = root.description && root.description !== root.name ? `${root.name} (${root.description})` : root.name;
        result.set(folder.id, { folderId: folder.id, rootId: root.id, path: [rootName, ...path.slice(1).map(entry => entry.name)].join(' / ') });
        break;
      }
      current = current.parentId && !ambiguous.has(current.parentId) ? byId.get(aliases.get(current.parentId) ?? '') : undefined;
    }
  }
  for (const [alias, id] of aliases) {
    const location = result.get(id);
    if (location && !ambiguous.has(alias)) result.set(alias, location);
  }
  for (const alias of ambiguous) result.delete(alias);
  return result;
}

/** Disabled picker rows and the execution guard share exactly the same discovered-store decision. */
export function folderTransferPlan(folders: readonly FolderTreeItem[], sourceFolderIds: readonly string[]) {
  const discovered = locations(folders);
  const roots = sourceFolderIds.map(id => discovered.get(id)?.rootId);
  const root = roots.length && roots.every(id => id !== undefined && id === roots[0]) ? roots[0] : undefined;
  const sources = new Set(sourceFolderIds.map(id => discovered.get(id)?.folderId));
  const selectable = new Set(folders.filter(folder => folder.selectable !== false && !folder.mailboxRoot).map(folder => folder.id));
  const canTransfer = (target: string): boolean => Boolean(root && selectable.has(target) && !sources.has(target) && discovered.get(target)?.rootId === root);
  return {
    folders: folders.map(folder => ({ ...folder, name: discovered.get(folder.id)?.path ?? folder.name, description: discovered.get(folder.id)?.path ?? folder.description })),
    disabledIds: folders.filter(folder => !canTransfer(folder.id)).map(folder => folder.id),
    canTransfer,
  };
}

/** Unknown search sources stay unknown; folder navigation must never substitute their current folder. */
export function transferSourceFolders(
  ids: readonly string[], folderId: string, searchItems?: readonly { id: string; folderId: string }[],
): string[] {
  if (!searchItems) return ids.map(() => folderId);
  const byId = new Map(searchItems.map(item => [item.id, item.folderId]));
  return ids.map(id => byId.get(id) ?? '');
}

/** Recheck discovery at execution time, including after a picker was opened. */
export async function transferWithinStore(
  folders: readonly FolderTreeItem[], sourceFolderIds: readonly string[], target: string, execute: () => Promise<void>,
): Promise<boolean> {
  if (!folderTransferPlan(folders, sourceFolderIds).canTransfer(target)) return false;
  await execute();
  return true;
}
